import { UsersService, initializeGroup, initializeService } from '@/service'
import {
  getSessionCookieData,
  isGokollab,
  isPwa,
} from '@ghl/clientportal-core/init'
import { computed, reactive } from 'vue'

import config from '@/config'
import { MemberStatus } from '@/models/Groups'
import restAgent from '@/service/restAgent'
import { useAppStore } from '@/store/app'
import { updateColorTokens } from '@/util/colorTokens'
import { getSessionLocationId } from '@ghl/clientportal-core'
import { GroupState } from './groups'
import { UserState } from './users'

class App {
  state = reactive({
    locationId: null,
    windowGradient: true,
    fetching: true,
    isPwa: true,
    isInitializationStatus: false,
    locationData: {} as any,
    isMobile: window.innerWidth <= 930,
    postContainer: null as any,
    learningContainer: null as any,
    memberListContainer: null as any,
    leaderBoardContainer: null as any,
    showGroupSettings: false,
    showInviteModal: false,
    memberSettingActiveTab: 'GroupInvitations',
    // keeping this is app state, same can be used to implement global search later on
    globalSearchText: null as string | null,
    groupSettingsActiveTab: 'GroupDetails',
    readingMode: window.innerWidth <= 1400,
    container: computed(() => {
      return this.state.postContainer
    }),
  })

  constructor() {
    this.addWindowResizeListener()
  }

  addWindowResizeListener() {
    window.addEventListener('resize', () => {
      this.state.isMobile = window.innerWidth <= 930

      this.state.readingMode =
        window.innerWidth <= 1330 &&
        GroupState.state?.userStatus === MemberStatus.ACTIVE
    })
  }

  async toggleReadingMode() {
    this.state.readingMode = !this.state.readingMode
  }
  openGroupSettings = (defaultTab?: string) => {
    if (defaultTab) {
      this.state.groupSettingsActiveTab = defaultTab
    }
    this.state.showGroupSettings = true
  }

  openInvitationModal = () => {
    this.state.showInviteModal = true
  }

  async createApp(route, router) {
    try {
      this.state.fetching = true
      this.state.isPwa = isPwa()
      await this.fetchLocationDetails()

      const appStore = useAppStore()
      if (this.state.locationId) {
        initializeService(this.state.locationId)
        appStore.setLocationId(this.state.locationId)
      }

      await Promise.all([
        this.fetchUserDetails(),
        this.fetchGroupDetails(route, router),
      ])

      // if (UserState.state.currentUser?.contactId) {
      //   UserState.state.currentUser.contactId =
      //     GroupState.state.userGroup?.contactId
      // }
      //TODO: Move this promise.all
      if (UserState.state.currentUser?.contactId) {
        await this.fetchUserGroups()
      }

      this.state.isInitializationStatus = true
      this.state.globalSearchText = null
      this.state.fetching = false
      UserState.setMandatoryInfoModal(
        !UserState.checkMandatedInfoPresentOrNot()
      )
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      this.state.fetching = false
    }
  }

  async fetchLocationDetails() {
    try {
      const page: any = JSON.parse(localStorage.getItem('event') as any)
      let domain: string = page?.domain || window.location.hostname
      if (domain === 'localhost') {
        const urlParams = new URLSearchParams(window.location.search)
        const domainname = urlParams.get('domain')
        domain = domainname || 'communities.staging.clientclub.net'
      }

      this.state.locationData = isGokollab()
        ? await this.getGokollabLocationDetails()
        : await this.getNonGokollabLocationDetails(domain)

      useAppStore().setPermissions(this.state.locationData)
      useAppStore().setLanguagePreference(
        this.state.locationData?.languagePreferenceBuilder
      )
      this.state.locationId = this.state.locationData?.locationId
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getNonGokollabLocationDetails(domain: string) {
    if (isPwa()) {
      const locationId = getSessionLocationId()
      if (locationId) {
        return await this.getNonGokollabLocationDetailsByLocationId(locationId)
      }
    }
    return await this.getNonGokollabLocationDetailsByDomain(domain)
  }

  async getNonGokollabLocationDetailsByDomain(domain: string) {
    const windowPortalSettings = (window as any)?.__preloadedApiData
      ?.portalSettings
    if (windowPortalSettings && windowPortalSettings?.locationId) {
      console.info('>>>>> Using preloaded portal settings: communities <<<<<')
      return windowPortalSettings
    }
    return (await restAgent.ClientPortal.getLocationDetails(domain)).data
  }

  async getNonGokollabLocationDetailsByLocationId(locationId: string) {
    const windowPortalSettings = (window as any)?.__preloadedApiData
      ?.portalSettings
    if (windowPortalSettings && windowPortalSettings?.locationId) {
      console.info('>>>>> Using preloaded portal settings: communities <<<<<')
      return windowPortalSettings
    }
    return (
      await restAgent.ClientPortal.getLocationDetailsByLocationId(locationId)
    ).data
  }

  async fetchGroupDetails(route, router) {
    try {
      const promises = [GroupState.getGroupByLocation({ limit: 15 })]

      if (route?.params?.groupSlug) {
        promises.push(initializeGroup(route.params?.groupSlug as string))
      }
      const result = await Promise.allSettled(promises)

      //TODO: Open incorrect group slug page or no such page found page

      if (result[1]?.status === 'rejected' || !route?.params?.groupSlug) {
        if (isGokollab()) {
          window.location.href = '/discovery'
        }

        await initializeGroup(GroupState.state?.groupList[0]?.slug as string)
        await router.push({
          name: 'home',
          params: { groupSlug: GroupState.state?.groupList[0]?.slug },
        })
      }

      if (route.name !== 'private-group' && route.name !== 'inactive-group') {
        this.state.readingMode = false
      }

      return
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async fetchUserDetails() {
    try {
      const cookie = getSessionCookieData()
      if (!cookie) {
        return
      }

      const appStore = useAppStore()
      if (this.state.locationId && cookie?.contactId) {
        appStore.setUserId(cookie?.contactId)
        const user = await UserState.getCurrentUser()
        if (user?.contactId) {
          //enableAnalytics(user.contactId)
          appStore.setAuthenticated(true)
          appStore.setUserId(user.contactId)
          updateColorTokens(
            UserState.state?.currentUser?.darkMode ? 'dark' : 'light'
          )
        }
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async fetchUserGroups() {
    try {
      const userGroups = await UsersService.getUserGroups(
        UserState.state.currentUser?.contactId as string
      )
      GroupState.state.userGroup = userGroups
      GroupState.state.role = (userGroups && userGroups?.role) || null
      GroupState.state.userStatus = userGroups?.status
        ? userGroups.status || MemberStatus.ACTIVE
        : undefined
      if (UserState.state.currentUser) {
        UserState.state.currentUser.contactId = userGroups.contactId
        useAppStore().setUserId(userGroups.contactId)
      }

      if (userGroups.membershipAnswers) {
        GroupState.state.userMembershipAnswers = userGroups.membershipAnswers
      }
    } catch (error) {
      console.error(error)
    }
  }

  async getGokollabLocationDetails() {
    return {
      _id: config.GOKOLLAB_LOCATION_ID,
      locationId: config.GOKOLLAB_LOCATION_ID,
      branding: {
        brandColor1: '',
      },
    }
  }
}

export const app = new App()
